import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { gbp, imgUrl, typeLabel, waLink, years } from "../../lib/api";
import { useCart } from "../../lib/cart";

export const ProductCard = ({ p, index = 0 }) => {
  const { add } = useCart();
  const inStock = (p?.stock ?? 0) > 0;
  return (
    <motion.article
      data-testid={`product-card-${p.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group flex flex-col"
    >
      <Link to={`/product/${p.id}`} data-testid={`product-card-link-${p.id}`} className="clip-frame relative block aspect-[4/5] overflow-hidden bg-[#121820]">
        <img
          src={imgUrl(p.images?.[0])}
          alt={p.title}
          loading="lazy"
          className="h-full w-full object-cover opacity-80 grayscale-[35%] transition-[transform,opacity,filter] duration-700 group-hover:scale-105 group-hover:opacity-100 group-hover:grayscale-0"
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,transparent_20%,rgba(9,13,18,0.85)_85%)]" />
        <span className="absolute left-3 top-3 rounded-full bg-[#090D12]/80 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-slate-200 backdrop-blur">
          {typeLabel(p.airbag_type)}
        </span>
        <span
          className={`absolute bottom-3 left-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest ${inStock ? "text-emerald-400" : "text-slate-400"}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${inStock ? "bg-emerald-400" : "bg-slate-500"}`} />
          {inStock ? `${p.stock} in stock` : "Sold out"}
        </span>
        <ArrowUpRight className="absolute right-4 top-4 h-5 w-5 translate-y-1 text-white opacity-0 transition-[transform,opacity] duration-500 group-hover:translate-y-0 group-hover:opacity-100" />
      </Link>
      <div className="flex flex-1 flex-col pt-4">
        <p className="font-mono text-xs uppercase tracking-widest text-slate-400">
          {p.make} {p.model} {years(p) && `· ${years(p)}`}
        </p>
        <Link to={`/product/${p.id}`} className="mt-1 font-display text-xl font-bold uppercase leading-tight tracking-tight hover:text-[#F43F5E]">
          {p.title}
        </Link>
        <div className="mt-auto flex items-end justify-between gap-2 pt-4">
          <p data-testid={`product-card-price-${p.id}`} className="font-display text-2xl font-bold">
            {gbp(p.price)}
          </p>
          <div className="flex gap-2">
            <a
              href={waLink(`Hi CBLS AIRBAGS, is this still available? ${p.title} (Part ${p.part_number || "n/a"})`)}
              target="_blank"
              rel="noreferrer"
              data-testid={`product-card-whatsapp-${p.id}`}
              aria-label="Enquire on WhatsApp"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 transition-colors hover:border-emerald-400 hover:text-emerald-400"
            >
              <MessageCircle className="h-4 w-4" />
            </a>
            <button
              data-testid={`product-card-add-${p.id}`}
              disabled={!inStock}
              onClick={() => add(p)}
              className="h-10 rounded-full bg-[#E11D48] px-4 text-sm font-semibold transition-[background-color,transform] hover:bg-[#F43F5E] active:scale-95 disabled:cursor-not-allowed disabled:bg-slate-700"
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
};
